import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { TypographyVariant, WithTheme, interpolateFont } from '@mint/core';
import styled from 'styled-components';

const pickFont = (props: WithTheme<{ dense?: boolean }>): TypographyVariant =>
  props.dense ? 'dense' : 'control';

const Sizer = styled.div`
  border: 0;
  margin-left: -9999px;
  outline: 0;
  overflow-wrap: break-word;
  padding: 0;
  position: absolute;
  visibility: hidden;
  white-space: pre-wrap;
  width: 100%;
  word-wrap: break-word;
  transform: rotate3d(0);
  ${interpolateFont(pickFont, 'text.primary')}
`;

export const useTextareaSizer = (
  value: string,
  update: (height: number) => void,
): React.ReactElement => {
  const ref = useRef() as React.RefObject<HTMLDivElement>;

  const __html =
    typeof value === 'string'
      ? value.replace(/\n/g, '<br/>&nbsp;')
      : '<br/>&nbsp;';

  const updateSizeCallback = useCallback((): void => {
    if (ref.current) {
      ref.current.innerHTML = __html;
      const { height } = ref.current.getBoundingClientRect();
      update(height);
    }
  }, [value]);

  useEffect((): (() => void) => {
    window.addEventListener('resize', updateSizeCallback);
    return (): void => window.removeEventListener('resize', updateSizeCallback);
  }, [updateSizeCallback]);

  useLayoutEffect(updateSizeCallback, [value]);

  return useMemo((): React.ReactElement => <Sizer ref={ref} />, []);
};
